// Адаптер WS-протокола: единственное место, где «проводной» формат сообщений
// бэкенда превращается в доменные SessionEvent и обратно. Точный формат пока
// неизвестен — здесь реализован разумный JSON-протокол по умолчанию. Когда
// придёт дока бэкенда, правки нужны ТОЛЬКО в этом файле, UI не трогаем.

import type { AppConfig } from '../config'
import type { AgentStatus, SessionEvent } from '../types'

export interface OutgoingText {
  kind: 'text'
  text: string
}
export interface OutgoingInterrupt {
  kind: 'interrupt'
}
export interface OutgoingControl {
  kind: 'start' | 'stop'
}
export type OutgoingControlMessage = OutgoingText | OutgoingInterrupt | OutgoingControl

export interface ProtocolAdapter {
  /** Бинарный аудио-кадр для отправки (или JSON-строка, если sendFormat=base64). */
  encodeAudio(pcm: Int16Array): ArrayBuffer | string
  /** Управляющее сообщение → строка для WS. */
  encodeControl(msg: OutgoingControlMessage): string
  /** Входящее WS-сообщение (строка или бинарь) → доменные события. */
  decode(data: string | ArrayBuffer): SessionEvent[]
}

/**
 * Протокол по умолчанию.
 *
 * Исходящее:
 *   - аудио: бинарные кадры Int16 PCM (sendFormat=pcm16),
 *     либо {type:'audio', data:'<base64>'} (sendFormat=base64).
 *   - текст: {type:'text', text}
 *   - управление: {type:'start'|'stop'|'interrupt'}
 *
 * Входящее (JSON):
 *   {type:'user_transcript', text, final?}
 *   {type:'agent_text', text|delta, final?}
 *   {type:'agent_audio', data:'<base64 pcm16/encoded>'}
 *   {type:'agent_speech_start'} / {type:'agent_speech_end'}
 *   {type:'interrupted'}
 *   {type:'agent_status', status:{...}}
 *   {type:'error', message}
 * Входящие бинарные кадры трактуются как чанки аудио агента.
 */
export class DefaultProtocol implements ProtocolAdapter {
  constructor(private cfg: AppConfig) {}

  encodeAudio(pcm: Int16Array): ArrayBuffer | string {
    if (this.cfg.audio.sendFormat === 'base64') {
      return JSON.stringify({ type: 'audio', data: int16ToBase64(pcm) })
    }
    // Гарантированно отдаём именно нужный кусок буфера.
    return (pcm.buffer as ArrayBuffer).slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength)
  }

  encodeControl(msg: OutgoingControlMessage): string {
    switch (msg.kind) {
      case 'text':
        return JSON.stringify({ type: 'text', text: msg.text })
      case 'interrupt':
        return JSON.stringify({ type: 'interrupt' })
      default:
        return JSON.stringify({ type: msg.kind })
    }
  }

  decode(data: string | ArrayBuffer): SessionEvent[] {
    if (data instanceof ArrayBuffer) {
      return [{ type: 'agent_audio', chunk: data }]
    }
    let msg: Record<string, unknown>
    try {
      msg = JSON.parse(data)
    } catch {
      return [{ type: 'error', message: 'Не удалось разобрать сообщение от сервера' }]
    }

    const type = String(msg.type ?? '')
    switch (type) {
      case 'user_transcript':
        return [{ type: 'user_transcript', text: str(msg.text), final: Boolean(msg.final) }]
      case 'agent_text':
        return [{ type: 'agent_text', text: str(msg.text ?? msg.delta), final: Boolean(msg.final) }]
      case 'agent_audio':
        return [{ type: 'agent_audio', chunk: base64ToArrayBuffer(str(msg.data)) }]
      case 'agent_speech_start':
        return [{ type: 'agent_speech_start' }]
      case 'agent_speech_end':
        return [{ type: 'agent_speech_end' }]
      case 'interrupted':
        return [{ type: 'interrupted' }]
      case 'agent_status':
        return [{ type: 'agent_status', status: normalizeStatus(msg.status) }]
      case 'error':
        return [{ type: 'error', message: str(msg.message) || 'Ошибка сервера' }]
      default:
        return [] // неизвестный тип — молча игнорируем
    }
  }
}

export function normalizeStatus(raw: unknown): AgentStatus {
  const s = (raw ?? {}) as Record<string, unknown>
  const stateRaw = String(s.state ?? s.status ?? 'unknown').toLowerCase()
  const allowed = ['idle', 'working', 'waiting', 'done', 'error']
  const state = (allowed.includes(stateRaw) ? stateRaw : 'unknown') as AgentStatus['state']
  return {
    state,
    task: s.task != null ? String(s.task) : undefined,
    progress: typeof s.progress === 'number' ? clamp01(s.progress) : undefined,
    lastResult: s.lastResult != null ? String(s.lastResult) : s.last_result != null ? String(s.last_result) : undefined,
    updatedAt: Date.now(),
  }
}

function str(v: unknown): string {
  return v == null ? '' : String(v)
}
function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n > 1 ? n / 100 : n))
}

// ── base64 <-> бинарь (для аудио в JSON-режиме) ──────────────────────────────

function int16ToBase64(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin)
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes.buffer
}
